import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createOrgSchema } from '@tyto/shared';
import { PlusCircle, Loader2 } from 'lucide-react';
import { z } from 'zod';

// Types
export type OrgFormValues = z.infer<typeof createOrgSchema>;

interface AdminOrgCreateProps { }

export const AdminOrgCreate: React.FC<AdminOrgCreateProps> = () => {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  const createForm = useForm<OrgFormValues>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: {
      name: '',
      slug: '',
      telegramChatId: '',
    },
  });

  const createOrgMutation = useMutation({
    mutationFn: async (data: OrgFormValues) => {
      const response = await fetch('/api/org', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create organization');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setIsCreateOpen(false);
      createForm.reset();
    },
  });

  const onCreateSubmit = (data: OrgFormValues) => {
    createOrgMutation.mutate(data);
  };

  return (
    <div className="max-w-xl mx-auto py-10">
      <div className="mb-6 flex items-center gap-2">
        <a href="/app/admin/orgs/manage" className="text-primary hover:underline flex items-center">
          ← Back to Organizations
        </a>
      </div>
      <h1 className="text-3xl font-bold mb-2">Create New Organization</h1>
      <p className="text-muted-foreground mb-8">Add a new organization to the system.</p>
      <Form {...createForm}>
        <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-4">
          <FormField
            control={createForm.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Organization name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={createForm.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl>
                  <Input placeholder="organization-slug" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={createForm.control}
            name="telegramChatId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telegram Chat ID (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="Telegram chat ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <SheetFooter>
            <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createOrgMutation.isPending}>
              {createOrgMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </SheetFooter>
        </form>
      </Form>
    </div>
  );
};
